import React, { useState, useCallback, useContext, useEffect } from "react"
import { parseExpression, Expression, Constant } from "tadiff"
import { AppContext, AppEnergyTerm } from "./Context"

type EnergyTermProps = {
    index: number
}

function makeEnergyTerm(expr: Expression, formula: string): AppEnergyTerm {
    return {
        expression: expr,
        expressionFormula: formula,
        affectedObjects: {},
        affectedObjectPairs: {}
    }
}

function AffectedObjectList(props: EnergyTermProps) {
    const { state, dispatch } = useContext(AppContext)!

    const index = props.index

    const energyTerm = state.energyTerms[index]

    const checkAll = useCallback(() => {
        const term = { ...energyTerm }
        state.objects.forEach((_, objectIndex) => {
            term.affectedObjects[objectIndex] = true
        })

        dispatch({
            type: "updateEnergyTerm",
            index: index,
            term: term
        })
    }, [state.objects, energyTerm, dispatch, index])

    return (
        <div>
            <input type="button" value="Check all" onClick={checkAll} />
            {state.objects.map((object, objectIndex) => {
                const isAffected = !!energyTerm.affectedObjects[objectIndex]

                const onChange = () => dispatch({
                    type: "updateEnergyTerm",
                    index: index,
                    term: {
                        ...energyTerm,
                        affectedObjects: {
                            ...energyTerm.affectedObjects,
                            [objectIndex]: !isAffected
                        }
                    }
                })

                return <input type="checkbox" key={objectIndex} checked={isAffected} onChange={onChange} />
            })}
        </div>
    )
}

function AffectedObjectPairList(props: EnergyTermProps) {
    const { state, dispatch } = useContext(AppContext)!

    const index = props.index

    const energyTerm = state.energyTerms[index]

    const checkPairs = useCallback(() => {
        const term = { ...energyTerm }
        state.objects.forEach((_, objectIndex) => {
            state.objects.forEach((_, otherObjectIndex) => {
                if (objectIndex !== otherObjectIndex) {
                    term.affectedObjectPairs[[objectIndex, otherObjectIndex].toString()] = true
                }
            })
        })

        dispatch({
            type: "updateEnergyTerm",
            index: index,
            term: term
        })
    }, [state.objects, energyTerm, dispatch, index])

    return (
        <div>
            <input type="button" value="Check pairs" onClick={checkPairs} />
            {state.objects.map((object, objectIndex) =>
                <div key={objectIndex}>
                    {state.objects.map((otherObject, otherObjectIndex) => {
                        const pairIndex = [objectIndex, otherObjectIndex].toString()
                        const isAffected = !!energyTerm.affectedObjectPairs[pairIndex]

                        const onChange = () => dispatch({
                            type: "updateEnergyTerm",
                            index: index,
                            term: {
                                ...energyTerm,
                                affectedObjectPairs: {
                                    ...energyTerm.affectedObjectPairs,
                                    [pairIndex]: !isAffected
                                }
                            }
                        })

                        return <input type="checkbox" key={otherObjectIndex} checked={isAffected} onChange={onChange} />
                    })}
                </div>
            )}
        </div>
    )
}

function EnergyTerm(props: EnergyTermProps) {
    const { state, dispatch } = useContext(AppContext)!

    const [formula, setFormula] = useState("")
    const [parsed, setParsed] = useState(true)
    const index = props.index

    const energyTerm = state.energyTerms[index]

    useEffect(() => {
        if (formula === "") {
            setFormula(energyTerm.expressionFormula)
        }
    }, [energyTerm, formula])

    const parseFormula = useCallback(() => {
        const energyExpression = parseExpression(formula)
        setParsed(true)
        dispatch({ type: "updateEnergyTerm", index: index, term: { ...energyTerm, expression: energyExpression, expressionFormula: formula } })
    }, [formula, index, dispatch, energyTerm])

    const onFormulaChanged = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setFormula(e.target.value)
        setParsed(false)
    }, [])

    return (
        <div style={{ border: "2px solid black", padding: "4px", margin: "4px" }}>
            <h2>Energy {index}</h2>
            <input type="text" style={{ width: 800, color: parsed ? "black" : "red" }} onChange={onFormulaChanged} value={formula} />
            <input type="button" value="Parse" onClick={parseFormula} />

            <h3>Affected objects</h3>
            <AffectedObjectList index={index} />

            <h3>Affected object pairs</h3>
            <AffectedObjectPairList index={index} />
        </div>
    )
}

export default function EnergyTermList() {
    const { state, dispatch } = useContext(AppContext)!

    const energyTerms = state.energyTerms

    return (
        <div style={{}}>
            <h1>Energy terms</h1>
            <div>
                {energyTerms.map((term, index) => <EnergyTerm key={index} index={index} />)}
            </div>
            <input type="button" value="Add" onClick={() => dispatch({ type: "addEnergyTerm", term: makeEnergyTerm(new Constant(0), "0") })} />
            <input type="button" value="Remove" onClick={() => dispatch({ type: "removeEnergyTerm" })} />
        </div>
    )
}
